import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const AuditQueue = ({ onBulkVerify }) => {
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'priority', direction: 'asc' });

  const auditQueue = [
    {
      id: 'AT-2025-0156',
      assetType: 'Laptop',
      location: 'Floor 2 - Room 201',
      lastVerified: '2024-11-15',
      status: 'Pending',
      priority: 'High',
      assignedTo: 'Arjun Sharma',
      daysOverdue: 5
    },
    {
      id: 'AT-2025-0089',
      assetType: 'Server',
      location: 'Data Center - Rack A3',
      lastVerified: '2024-12-01',
      status: 'In Progress',
      priority: 'Critical',
      assignedTo: 'Priya Patel',
      daysOverdue: 12
    },
    {
      id: 'AT-2025-0234',
      assetType: 'Printer',
      location: 'Floor 1 - Reception',
      lastVerified: '2025-01-10',
      status: 'Pending',
      priority: 'Medium',
      assignedTo: 'Rahul Verma',
      daysOverdue: 2
    },
    {
      id: 'AT-2025-0178',
      assetType: 'Monitor',
      location: 'Floor 3 - Conference Room',
      lastVerified: '2024-10-28',
      status: 'Pending',
      priority: 'High',
      assignedTo: 'Anjali Singh',
      daysOverdue: 8
    },
    {
      id: 'AT-2025-0067',
      assetType: 'Router',
      location: 'IT Room - Network Closet',
      lastVerified: '2025-02-01',
      status: 'Verified',
      priority: 'Low',
      assignedTo: 'Vikram Nair',
      daysOverdue: 0
    }
  ];

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedAssets(auditQueue?.map(asset => asset?.id));
    } else {
      setSelectedAssets([]);
    }
  };

  const handleSelectAsset = (assetId, checked) => {
    if (checked) {
      setSelectedAssets(prev => [...prev, assetId]);
    } else {
      setSelectedAssets(prev => prev?.filter(id => id !== assetId));
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'bg-error text-error-foreground';
      case 'High':
        return 'bg-warning text-warning-foreground';
      case 'Medium':
        return 'bg-accent text-accent-foreground';
      case 'Low':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Verified':
        return 'bg-success text-success-foreground';
      case 'In Progress':
        return 'bg-accent text-accent-foreground';
      case 'Pending':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const sortedQueue = [...auditQueue]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Audit Queue</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {auditQueue?.length} assets requiring verification
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              disabled={selectedAssets?.length === 0}
              onClick={() => onBulkVerify(selectedAssets)}
            >
              <Icon name="CheckSquare" size={16} className="mr-2" />
              Bulk Verify ({selectedAssets?.length})
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="Download" size={16} className="mr-2" />
              Export Queue
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-12 p-4">
                <Checkbox
                  checked={selectedAssets?.length === auditQueue?.length}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th 
                className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-smooth"
                onClick={() => handleSort('id')}
              >
                <div className="flex items-center space-x-1">
                  <span>Asset ID</span>
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th 
                className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-smooth"
                onClick={() => handleSort('assetType')}
              >
                <div className="flex items-center space-x-1">
                  <span>Type</span>
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th 
                className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-smooth"
                onClick={() => handleSort('location')}
              >
                <div className="flex items-center space-x-1">
                  <span>Location</span>
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th 
                className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-smooth"
                onClick={() => handleSort('lastVerified')}
              >
                <div className="flex items-center space-x-1">
                  <span>Last Verified</span>
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">Priority</th>
              <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
              <th className="text-left p-4 font-medium text-muted-foreground">Assigned To</th>
              <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedQueue?.map((asset) => (
              <tr key={asset?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="p-4">
                  <Checkbox
                    checked={selectedAssets?.includes(asset?.id)}
                    onChange={(e) => handleSelectAsset(asset?.id, e?.target?.checked)}
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-card-foreground">{asset?.id}</span>
                    {asset?.daysOverdue > 0 && (
                      <span className="bg-error text-error-foreground text-xs px-2 py-1 rounded-full">
                        {asset?.daysOverdue}d overdue
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-4 text-card-foreground">{asset?.assetType}</td>
                <td className="p-4 text-card-foreground">{asset?.location}</td>
                <td className="p-4 text-muted-foreground">{asset?.lastVerified}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(asset?.priority)}`}>
                    {asset?.priority}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset?.status)}`}>
                    {asset?.status}
                  </span>
                </td>
                <td className="p-4 text-card-foreground">{asset?.assignedTo}</td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-2">
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Icon name="CheckCircle" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditQueue;